import React, { useState, useEffect } from 'react';
import { ChartStyle, CompleteKundali, BirthDetails, Language, NodeConvention } from './types/kundali';
import { calculateKundali, getActiveAyanamshaPreference, getActiveNodeConvention } from './utils/vedicEngine';
import { ZODIAC_HI, getNakshatraName, getGenderName } from './utils/language';
import { formatDOB } from './utils/dateUtils';
import { UserProfile, auth, syncUserProfile, checkRedirectResult, logoutUser } from './lib/firebase';
import { getUserSavedKundalis, getAllKundalisAdmin, saveKundaliToCloud, trackKundaliAccess, cleanupAndReassignNamedKundalis } from './lib/kundaliService';
import { deduplicateKundalis, getBirthDetailsKey } from './utils/kundaliDedupe';
import { onAuthStateChanged } from 'firebase/auth';
import { Header } from './components/Header';
import { BirthFormModal } from './components/BirthFormModal';
import { StartPopupModal } from './components/StartPopupModal';
import { ChartViewer } from './components/ChartViewer';
import { PlanetaryTable } from './components/PlanetaryTable';
import { GocharView } from './components/GocharView';
import { DashaTimeline } from './components/DashaTimeline';
import { DoshaSection } from './components/DoshaSection';
import { AshtakvargaView } from './components/AshtakvargaView';
import { ShadbalaView } from './components/ShadbalaView';
import { PrashnaModule } from './components/PrashnaModule';
import { RemediesView } from './components/RemediesView';
import { GemstoneAdvisor } from './components/GemstoneAdvisor';
import { ShubhaEssentialsHub } from './components/ShubhaEssentialsHub';
import { SwastikLogo } from './components/SwastikLogo';
import { UserRole, AccountTier } from './types/user';
import { SimpleAnalysisView } from './components/SimpleAnalysisView';
import { PanchangMuhurtaView } from './components/PanchangMuhurtaView';
import { MatchingModule } from './components/MatchingModule';
import { NumerologyView } from './components/NumerologyView';
import { PalmistryView } from './components/PalmistryView';
import { VastuView } from './components/VastuView';
import { GeminiAstrologer } from './components/GeminiAstrologer';
import { NavtaraView } from './components/NavtaraView';
import { MuhurtaAdvisor } from './components/MuhurtaAdvisor';
import { KarkaView } from './components/KarkaView';
import { DashaChartView } from './components/DashaChartView';
import { AuthModal } from './components/AuthModal';
import { AdminPortal } from './components/AdminPortal';
import { MyKundalisDrawer } from './components/MyKundalisDrawer';
import { LoginPage } from './components/LoginPage';
import { ContactModal } from './components/ContactModal';
import { WhatsAppIntegrationModal } from './components/WhatsAppIntegrationModal';
import { BtrDrawer } from './components/BtrDrawer';
import { getBtrProfileInstanceKey } from './utils/btrProfileIdentity';
import { mergeBtrProvenanceForBirthEdit } from './utils/btrProvenance';
import { PrintReport } from './components/PrintReport';
import { TrinesView } from './components/TrinesView';
import { PlanetStrengthDashboard } from './components/PlanetStrengthDashboard';
import { AnalyticsReportView } from './components/AnalyticsReportView';
import { AstrologerCoreView } from './components/AstrologerCoreView';
import BirthTimeRectification from './components/BirthTimeRectification';
import VedicAnalysisEngine from './components/VedicAnalysisEngine';
import { BackupRestoreModal } from './components/BackupRestoreModal';
import { GoldenKundaliModal } from './components/GoldenKundaliModal';
import { KPView } from './components/KPView';
import { SpecializedDiagnosticsView } from './components/SpecializedDiagnosticsView';
import { AdvancedLifeAnalysisView } from './components/AdvancedLifeAnalysisView';
import { AstrologerDiagnosticDossier } from './components/AstrologerDiagnosticDossier';
import { BasicAnalysisView } from './components/role/BasicAnalysisView';
import { KundaliWorkbench } from './components/KundaliWorkbench';
import { VedicMilestoneDestinyStudio } from './components/v520/VedicMilestoneDestinyStudio';
import { AuspiciousEssentialsBar } from './components/AuspiciousEssentialsBar';
import { SpiritualityIntelligenceView } from './components/v600/SpiritualityIntelligenceView';
import { TodaysPanchangWidget } from './components/TodaysPanchangWidget';
import { NewProfessionalDashboard } from './components/NewProfessionalDashboard';
import { FeatureNavigationStrip } from './components/FeatureNavigationStrip';
import { PublicationDossierView } from './components/PublicationDossierView';
import { JatakaProfileHeader } from './components/JatakaProfileHeader';
import { UserProfileRecord } from './types/user';
import { sanitizeSessionUser, canSwitchRole, switchActiveRole, getRoleLabel, isBasicTierUser, isSuperAdminUser } from './utils/rbac';
import {
  PlusCircle,
  BookOpen,
  Compass,
  Sparkles,
  Printer,
  User,
  Calendar,
  Users,
  Gem,
  Hash,
  Hand,
  Home,
  Clock,
  FileText,
  BarChart3,
  Layers,
  Activity,
  Edit3,
  Star
} from 'lucide-react';

function getActiveCalculationContext(): { nodeConvention: NodeConvention; ayanamsha: string } {
  return {
    nodeConvention: getActiveNodeConvention(),
    ayanamsha: getActiveAyanamshaPreference()
  };
}

export default function App() {
  const [activeProfile, setActiveProfile] = useState<UserProfileRecord | null>(null);
  const [viewMode, setViewMode] = useState<'simple' | 'astrologer'>(() => {
    try {
      const savedMode = localStorage.getItem('astro_view_mode');
      if (savedMode === 'simple' || savedMode === 'astrologer') return savedMode;
      const storedUser = localStorage.getItem('astro_custom_session');
      if (storedUser) {
        const parsed = JSON.parse(storedUser);
        if (['super_admin', 'admin', 'super_user'].includes(String(parsed.role))) return 'astrologer';
      }
    } catch {}
    return 'simple';
  });

  const [activeTab, setActiveTab] = useState<string>('enterprise-studio');
  const [chartStyle, setChartStyle] = useState<ChartStyle>('north');
  const [showAskAcharyaWidget, setShowAskAcharyaWidget] = useState<boolean>(() => {
    return localStorage.getItem('astro_show_ask_acharya') !== 'false';
  });

  useEffect(() => {
    const handleStorageChange = () => {
      setShowAskAcharyaWidget(localStorage.getItem('astro_show_ask_acharya') !== 'false');
    };
    window.addEventListener('astro_show_ask_acharya_changed', handleStorageChange);
    return () => window.removeEventListener('astro_show_ask_acharya_changed', handleStorageChange);
  }, []);

  // Listen for global calculation preference changes (e.g. Node convention or Ayanamsha in SettingsModal)
  useEffect(() => {
    const handleCalculationPrefChanged = () => {
      setKundali((prev) => {
        if (!prev?.birthDetails) return prev;
        return calculateKundali(prev.birthDetails, getActiveCalculationContext());
      });
    };
    window.addEventListener('astro_calculation_pref_changed', handleCalculationPrefChanged);
    return () => {
      window.removeEventListener('astro_calculation_pref_changed', handleCalculationPrefChanged);
    };
  }, []);
  const [lang, setLang] = useState<Language>(() => {
    try {
      const saved = localStorage.getItem('kundali_lang');
      if (saved === 'hi' || saved === 'en') return saved;
    } catch {}
    return 'en';
  });

  // Persist language in localStorage
  useEffect(() => {
    try {
      localStorage.setItem('kundali_lang', lang);
    } catch {}
  }, [lang]);

  // Persist view mode in localStorage
  useEffect(() => {
    try {
      localStorage.setItem('astro_view_mode', viewMode);
    } catch {}
  }, [viewMode]);

  // Global tab navigation event listener
  useEffect(() => {
    const handleNavigateTab = (e: Event) => {
      const customEvent = e as CustomEvent<string>;
      if (customEvent.detail) {
        setActiveTab(customEvent.detail);
      }
    };
    window.addEventListener('astro_navigate_tab', handleNavigateTab);
    return () => window.removeEventListener('astro_navigate_tab', handleNavigateTab);
  }, []);
  const [isBirthFormOpen, setIsBirthFormOpen] = useState<boolean>(false);
  const [editingBirthDetails, setEditingBirthDetails] = useState<BirthDetails | null>(null);
  const [editingOriginalName, setEditingOriginalName] = useState<string | null>(null);
  const [activeKundaliId, setActiveKundaliId] = useState<string | null>(null);
  const [isStartPopupOpen, setIsStartPopupOpen] = useState<boolean>(true);
  const [isPrintView, setIsPrintView] = useState<boolean>(false);
  const [isLoginPage, setIsLoginPage] = useState<boolean>(false);
  const [isContactModalOpen, setIsContactModalOpen] = useState<boolean>(false);
  const [isWhatsAppModalOpen, setIsWhatsAppModalOpen] = useState<boolean>(false);
  const [isBackupRestoreOpen, setIsBackupRestoreOpen] = useState<boolean>(false);
  const [isGoldenRegressionOpen, setIsGoldenRegressionOpen] = useState<boolean>(false);

  // Authentication & Profile States
  const [currentUser, setCurrentUser] = useState<UserProfile | null>(() => {
    try {
      const stored = localStorage.getItem('astro_custom_session');
      if (stored) {
        const parsed = JSON.parse(stored);
        return sanitizeSessionUser(parsed);
      }
    } catch (e) {
      console.error('Failed restoring custom session:', e);
    }
    return null;
  });

  const [isAuthModalOpen, setIsAuthModalOpen] = useState<boolean>(false);
  const [isAdminPortalOpen, setIsAdminPortalOpen] = useState<boolean>(false);
  const [isMyVaultOpen, setIsMyVaultOpen] = useState<boolean>(false);
  const [initialConsultationPrompt, setInitialConsultationPrompt] = useState<string>('');

  // Tester Role Switch Handler
  const handleSwitchRole = (targetRole: UserRole) => {
    if (!currentUser || !canSwitchRole(currentUser)) {
      console.warn('Unauthorized attempt to switch active role');
      return;
    }
    const updated = switchActiveRole(currentUser, targetRole);
    setCurrentUser(updated);
    try {
      localStorage.setItem('astro_custom_session', JSON.stringify(updated));
    } catch (e) {
      console.error('Failed to store session on role switch:', e);
    }
  };

  // Firebase Auth Listener & Redirect Handling
  const [hasFetchedAdminData, setHasFetchedAdminData] = useState(false);

  useEffect(() => {
    checkRedirectResult().then((user) => {
      if (user) {
        const sanitized = sanitizeSessionUser(user) || user;
        setCurrentUser(sanitized);
        localStorage.setItem('astro_custom_session', JSON.stringify(sanitized));
        setIsLoginPage(false);
      }
    }).catch(() => {
      // Ignored for iframe / IndexedDB lifecycle safely
    });

    const unsubscribe = onAuthStateChanged(auth, async (fbUser) => {
      if (fbUser) {
        // Simple check to avoid redundant syncing if profile is already loaded in session
        if (currentUser && currentUser.uid === fbUser.uid) return;
        
        try {
          const profile = await syncUserProfile({
            uid: fbUser.uid,
            email: fbUser.email || '',
            displayName: fbUser.displayName || '',
            photoURL: fbUser.photoURL || ''
          });
          const sanitized = sanitizeSessionUser(profile) || profile;
          setCurrentUser(sanitized);
          localStorage.setItem('astro_custom_session', JSON.stringify(sanitized));
        } catch (e) {
          console.error('Error syncing FB profile:', e);
        }
      } else {
        // User is logged out from Firebase: only clear if no custom or guest session is active
        const customSession = localStorage.getItem('astro_custom_session');
        if (!customSession) {
          setCurrentUser(null);
          localStorage.removeItem('astroveda_active_kundali');
        }
      }
    });
    return () => unsubscribe();
  }, []);

  // Initial Kundali state: restore active session or non-Aarav profile
  const [kundali, setKundali] = useState<CompleteKundali | null>(() => {
    try {
      const activeStored = localStorage.getItem('astroveda_active_kundali');
      if (activeStored) {
        const parsed: CompleteKundali = JSON.parse(activeStored);
        if (parsed?.birthDetails) {
          return calculateKundali(parsed.birthDetails, getActiveCalculationContext());
        }
      }
      const stored = localStorage.getItem('astroveda_saved_kundalis_guest');
      if (stored) {
        const parsed: CompleteKundali[] = JSON.parse(stored);
        const validCharts = parsed.filter(
          (k) => Boolean(k?.birthDetails?.name && k?.birthDetails?.date)
        );
        if (validCharts.length > 0 && validCharts[0]?.birthDetails) {
          return calculateKundali(validCharts[0].birthDetails, getActiveCalculationContext());
        }
      }
      // Fallback default sample Kundali for native preview and guest experience
      const defaultSample: BirthDetails = {
        name: 'Navya Gupta',
        gender: 'female',
        date: '2015-03-12',
        time: '14:13:00',
        latitude: 28.6139,
        longitude: 77.2090,
        placeName: 'Delhi, India',
        timezoneOffsetMinutes: 330
      };
      return calculateKundali(defaultSample, getActiveCalculationContext());
    } catch {
      // ignore
    }
    return null;
  });

  // Save active kundali to localStorage so current data is preserved on restart/refresh
  useEffect(() => {
    if (kundali) {
      try {
        localStorage.setItem('astroveda_active_kundali', JSON.stringify(kundali));
      } catch (e) {
        console.error('Failed saving active kundali:', e);
      }
    }
  }, [kundali]);

  // Authoritative Shared Target Analysis Moment across Dasha, Gochar, Charts, Evidence
  const [targetAnalysisDateTime, setTargetAnalysisDateTime] = useState<Date>(() => new Date());
  const [isBtrDrawerOpen, setIsBtrDrawerOpen] = useState<boolean>(false);

  // Legacy Dashboard Fallback Toggle (Default is New V5.16 Dashboard)
  const [isLegacyDashboard, setIsLegacyDashboard] = useState<boolean>(false);

  // RBAC-Compliant Legacy Dashboard Handler
  const handleOpenLegacyDashboard = (roleContext?: 'BASIC' | 'PRO' | 'ACHARYA' | 'MANAGEMENT') => {
    // Legacy dashboard access is strictly restricted to Super Admin only
    if (!isSuperAdminUser(currentUser)) {
      setIsLegacyDashboard(false);
      return;
    }
    const isBasic = isBasicTierUser(currentUser) || roleContext === 'BASIC';
    if (isBasic) {
      setViewMode('simple');
      setActiveTab('analytics');
    } else {
      setViewMode('astrologer');
      setActiveTab('charts');
    }
    setIsLegacyDashboard(true);
  };

  // Strict RBAC Enforcement for Legacy Dashboard: only Super Admin can stay in legacy mode
  useEffect(() => {
    if (isLegacyDashboard && !isSuperAdminUser(currentUser)) {
      setIsLegacyDashboard(false);
    }
    if (isBasicTierUser(currentUser)) {
      if (viewMode !== 'simple') {
        setViewMode('simple');
      }
      const basicTabs = ['enterprise-studio', 'workbench', 'analytics', 'advanced-life-analysis', 'simple-reading', 'ai-consult', 'panchang', 'daily-panchang', 'full-panchang', 'matching', 'publication-dossier', 'gemstones', 'remedies', 'shubha-essentials', 'navtara', 'muhurta', 'numerology', 'palmistry', 'vastu'];
      if (!basicTabs.includes(activeTab)) {
        setActiveTab('enterprise-studio');
      }
    }
  }, [currentUser, viewMode, activeTab, isLegacyDashboard]);

  // Saved Kundalis list (strictly custom created Kundalis)
  const [savedKundalis, setSavedKundalis] = useState<CompleteKundali[]>([]);

  // Load user-scoped local cache when user logs in/out/changes
  useEffect(() => {
    const cacheKey = currentUser?.uid 
      ? `astroveda_saved_kundalis_${currentUser.uid}` 
      : 'astroveda_saved_kundalis_guest';
    try {
      const stored = localStorage.getItem(cacheKey);
      if (stored) {
        const parsed: CompleteKundali[] = JSON.parse(stored);
        const computedList = parsed
          .filter((k) => Boolean(k?.birthDetails?.name && k?.birthDetails?.date))
          .map((k) => {
            try {
              return calculateKundali(k.birthDetails, getActiveCalculationContext());
            } catch {
              return k;
            }
          });
        const deduped = deduplicateKundalis(computedList);
        setSavedKundalis(deduped);
        setKundali((prev) => (prev ? prev : (deduped[0] || null)));
      } else {
        setSavedKundalis([]);
      }
    } catch (e) {
      console.error('Failed to load user-scoped saved kundalis:', e);
      setSavedKundalis([]);
    }
  }, [currentUser?.uid]);

  // Load saved Kundalis (all system Kundalis for Super Admin, or user-specific for Normal User)
  useEffect(() => {
    if (currentUser?.uid) {
      const isSuperAdmin = currentUser.role === UserRole.SUPER_ADMIN || (currentUser.role as string) === 'super_admin';
      if (isSuperAdmin && hasFetchedAdminData) return;

      const runFetch = () => {
        const fetchPromise = isSuperAdmin
          ? getAllKundalisAdmin()
          : getUserSavedKundalis(currentUser.uid);

        fetchPromise
          .then((records) => {
            if (records) {
              if (isSuperAdmin) setHasFetchedAdminData(true);
              // Recalculate each cloud record's full astrological charts and Ashtakavarga
              const cloudKundalis = records
                .filter((r) => Boolean(r?.kundaliData?.birthDetails?.name && r?.kundaliData?.birthDetails?.date))
                .map((r) => {
                  try {
                    const c = calculateKundali(r.kundaliData.birthDetails, getActiveCalculationContext());
                    c.id = r.id;
                    c.cloudId = r.id;
                    return c;
                  } catch {
                    return { ...r.kundaliData, id: r.id, cloudId: r.id };
                  }
                });

              setSavedKundalis(() => {
                // Deduplicate across any multi-profile records matching DOB, Time, and POB
                const deduped = deduplicateKundalis(cloudKundalis);
                setKundali((prev) => {
                  if (prev) {
                    if (prev.id) setActiveKundaliId(prev.id);
                    return prev;
                  }
                  const first = deduped[0] || null;
                  if (first?.id) setActiveKundaliId(first.id);
                  return first;
                });
                return deduped;
              });
            }
          })
          .catch((e) => console.error('Failed loading cloud kundalis:', e));
      };

      // Perform named Kundali cleanup and reassignment if current user is the target admin account
      if (currentUser.email === 'bijen.rohilla@gmail.com') {
        cleanupAndReassignNamedKundalis(currentUser)
          .then(() => {
            runFetch();
          })
          .catch((e) => {
            console.error('Failed executing profile cleanup/reassignment:', e);
            runFetch();
          });
      } else {
        runFetch();
      }
    }
  }, [currentUser?.uid, currentUser?.role]);

  // Persist saved kundalis to user-scoped localStorage (keeping max 5 recent items to avoid quota limit)
  useEffect(() => {
    const cacheKey = currentUser?.uid 
      ? `astroveda_saved_kundalis_${currentUser.uid}` 
      : 'astroveda_saved_kundalis_guest';
    try {
      const itemsToStore = deduplicateKundalis(savedKundalis).slice(0, 5);
      localStorage.setItem(cacheKey, JSON.stringify(itemsToStore));
    } catch (e: any) {
      if (e.name === 'QuotaExceededError' || e.code === 22 || e.code === 1014 || (e.message && e.message.includes('exceeded'))) {
        try {
          const minimalItems = deduplicateKundalis(savedKundalis).slice(0, 2);
          localStorage.setItem(cacheKey, JSON.stringify(minimalItems));
        } catch {
          // Ignore if still exceeds
        }
      } else {
        console.error('Failed to save user-scoped kundalis:', e);
      }
    }
  }, [savedKundalis, currentUser?.uid]);

  // Handle deleting a Kundali from app state and user-scoped cache
  const handleDeleteKundaliFromApp = (deletedId: string) => {
    setSavedKundalis((prev) => prev.filter((k) => k.id !== deletedId && (k as any).cloudId !== deletedId));
    if (activeKundaliId === deletedId) {
      setActiveKundaliId(null);
    }
    const cacheKey = currentUser?.uid 
      ? `astroveda_saved_kundalis_${currentUser.uid}` 
      : 'astroveda_saved_kundalis_guest';
    try {
      const cached = localStorage.getItem(cacheKey);
      if (cached) {
        const parsed = JSON.parse(cached);
        if (Array.isArray(parsed)) {
          const filtered = parsed.filter((k: any) => k.id !== deletedId && (k as any).cloudId !== deletedId);
          localStorage.setItem(cacheKey, JSON.stringify(filtered));
        }
      }
    } catch (e) {
      console.warn('Could not update localStorage cache after delete:', e);
    }
  };

  // Helper to download the generated Kundali report directly to local PC
  const downloadKundaliToLocalPC = (computed: CompleteKundali) => {
    try {
      const details = computed.birthDetails;
      const planets = computed.planets || [];
      
      let text = `==================================================\n`;
      text += `       ASTROVEDA VEDIC KUNDALI BIRTH REPORT       \n`;
      text += `==================================================\n\n`;
      text += `  PROFILE DETAILS:\n`;
      text += `  ------------------------------------------------\n`;
      text += `  Name:            ${details.name}\n`;
      text += `  Gender:          ${details.gender.toUpperCase()}\n`;
      text += `  Date of Birth:   ${details.date}\n`;
      text += `  Time of Birth:   ${details.time}\n`;
      text += `  Place of Birth:  ${details.placeName}\n`;
      text += `  Latitude:        ${details.latitude}°\n`;
      text += `  Longitude:       ${details.longitude}°\n`;
      text += `  Timezone Offset: GMT ${details.timezoneOffsetMinutes >= 0 ? '+' : ''}${(details.timezoneOffsetMinutes / 60).toFixed(1)} Hrs\n\n`;
      
      const ascendantPlanet = planets.find((p: any) => p.name === 'Ascendant');
      const lagnaSign = ascendantPlanet ? ascendantPlanet.sign : 'Aries';

      if (computed.panchang) {
        text += `  ASTRONOMICAL INFORMATION:\n`;
        text += `  ------------------------------------------------\n`;
        text += `  Nakshatra:       ${computed.panchang.nakshatra || 'Unknown'}\n`;
        text += `  Nakshatra Lord:  ${computed.avakhada?.nakshatraLord || 'Unknown'}\n`;
        text += `  Sunrise:         ${computed.panchang.sunrise || 'Unknown'}\n`;
        text += `  Sunset:          ${computed.panchang.sunset || 'Unknown'}\n`;
        text += `  Lagna (Ascendant): ${lagnaSign}\n\n`;
      }

      text += `  PLANETARY POSITIONS:\n`;
      text += `  ------------------------------------------------\n`;
      text += `  ${'Planet'.padEnd(12)} | ${'Rashi'.padEnd(12)} | ${'Degree'.padEnd(10)} | ${'House'.padEnd(6)}\n`;
      text += `  ------------------------------------------------\n`;
      planets.forEach((p: any) => {
        text += `  ${p.name.padEnd(12)} | ${p.sign.padEnd(12)} | ${p.degreeInSign.toFixed(2).toString().padEnd(10)} | House ${p.house}\n`;
      });
      text += `\n`;
      text += `==================================================\n`;
      text += `Generated automatically on AstroVeda. Saved to cloud.\n`;
      text += `==================================================\n`;

      const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${details.name.replace(/\s+/g, '_')}_AstroVeda_Kundali.txt`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading text report:', err);
    }
  };

  const handleOpenEditKundali = () => {
    if (kundali?.birthDetails) {
      setEditingBirthDetails(kundali.birthDetails);
      setEditingOriginalName(kundali.birthDetails.name || null);
      if (kundali.id) {
        setActiveKundaliId(kundali.id);
      }
      setIsBirthFormOpen(true);
    }
  };

  const handleOpenNewChart = () => {
    setEditingBirthDetails(null);
    setEditingOriginalName(null);
    setActiveKundaliId(null);
    setIsBirthFormOpen(true);
  };

  // Handle Form Submit for New or Edited Kundali (triggers complete recalculation)
  const handleBirthFormSubmit = async (details: BirthDetails) => {
    try {
      // Preserve the complete historical BTR audit record when the form submits only base birth inputs.
      // Date/time edits stale dependent results without deleting historical values or inventing provenance.
      const finalDetails = editingBirthDetails
        ? mergeBtrProvenanceForBirthEdit(details, editingBirthDetails)
        : { ...details };

      const computed = calculateKundali(finalDetails, getActiveCalculationContext());
      const targetRecordId = activeKundaliId || kundali?.id || kundali?.cloudId || undefined;
      if (targetRecordId) {
        computed.id = targetRecordId;
        computed.cloudId = targetRecordId;
      }
      setKundali(computed);

      const oldKey = editingBirthDetails ? getBirthDetailsKey(editingBirthDetails) : null;
      const newKey = getBirthDetailsKey(finalDetails);
      const prevName = (editingOriginalName || editingBirthDetails?.name || '').toLowerCase().trim();

      // Add to saved list, replacing the old entry if in edit mode
      setSavedKundalis((prev) => {
        if (targetRecordId) {
          const idx = prev.findIndex((k) => (k.id && k.id === targetRecordId) || (prevName && k.birthDetails.name?.toLowerCase().trim() === prevName));
          if (idx !== -1) {
            const nextList = [...prev];
            nextList[idx] = computed;
            return nextList;
          }
        }
        if (oldKey || prevName) {
          const idx = prev.findIndex((k) => (oldKey && getBirthDetailsKey(k.birthDetails) === oldKey) || (prevName && k.birthDetails.name?.toLowerCase().trim() === prevName));
          if (idx !== -1) {
            const nextList = [...prev];
            nextList[idx] = computed;
            return nextList;
          }
        }
        const exists = prev.some((k) => (targetRecordId && k.id === targetRecordId) || getBirthDetailsKey(k.birthDetails) === newKey);
        if (!exists) return [computed, ...prev];
        return prev.map((k) => (targetRecordId && k.id === targetRecordId ? computed : k));
      });

      // Auto-save to cloud if user is logged in (updating the existing document rather than creating a new profile)
      if (currentUser) {
        try {
          const savedRecord = await saveKundaliToCloud(
            computed,
            currentUser,
            targetRecordId,
            undefined,
            '',
            false,
            editingOriginalName || editingBirthDetails?.name || undefined
          );
          if (savedRecord?.id) {
            setActiveKundaliId(savedRecord.id);
            computed.id = savedRecord.id;
            computed.cloudId = savedRecord.id;
          }
        } catch (err) {
          console.error('Failed to auto-save Kundali to cloud:', err);
        }
      }

      setEditingBirthDetails(null);
      setEditingOriginalName(null);
      setIsBirthFormOpen(false);
      setActiveTab('analytics');
    } catch (err) {
      console.error('Error generating/recalculating Kundali:', err);
      alert('Failed to calculate Kundali. Please check birth details and try again.');
    }
  };

  // Handle applying rectified Kundali from BTR (persisting to state, savedKundalis list, localStorage cache, and Firestore cloud)
  const handleApplyRectifiedKundali = async (rectifiedKundali: CompleteKundali) => {
    try {
      setKundali(rectifiedKundali);

      const rectifiedName = (rectifiedKundali.birthDetails.name || '').toLowerCase().trim();

      // Update savedKundalis in state
      setSavedKundalis((prev) => {
        const idx = prev.findIndex(
          (k) => (k.birthDetails.name || '').toLowerCase().trim() === rectifiedName
        );
        if (idx !== -1) {
          const next = [...prev];
          next[idx] = rectifiedKundali;
          return next;
        }
        return [rectifiedKundali, ...prev];
      });

      // Update user-scoped localStorage cache
      const cacheKey = currentUser?.uid 
        ? `astroveda_saved_kundalis_${currentUser.uid}` 
        : 'astroveda_saved_kundalis_guest';
      try {
        const cached = localStorage.getItem(cacheKey);
        let list: CompleteKundali[] = cached ? JSON.parse(cached) : [];
        const idx = list.findIndex(
          (k) => (k.birthDetails.name || '').toLowerCase().trim() === rectifiedName
        );
        if (idx !== -1) {
          list[idx] = rectifiedKundali;
        } else {
          list.unshift(rectifiedKundali);
        }
        localStorage.setItem(cacheKey, JSON.stringify(list.slice(0, 10)));
      } catch (e) {
        console.warn('Could not update localStorage cache with rectified kundali:', e);
      }

      // Persist to Cloud Firestore database
      if (currentUser) {
        try {
          await saveKundaliToCloud(rectifiedKundali, currentUser);
          console.log(`[BTR] Successfully persisted rectified chart for ${rectifiedKundali.birthDetails.name} to Firestore.`);
        } catch (err) {
          console.error('[BTR] Error saving rectified chart to Firestore:', err);
        }
      }
    } catch (err) {
      console.error('[BTR] Failed in handleApplyRectifiedKundali:', err);
    }
  };

  const handleLogout = () => {
    setCurrentUser(null);
    setKundali(null);
    setActiveKundaliId(null);
    localStorage.removeItem('astro_custom_session');
    localStorage.removeItem('astroveda_active_kundali');
    logoutUser().catch(() => {});
  };

  if (isPrintView && kundali) {
    return <PrintReport kundali={kundali} onBack={() => setIsPrintView(false)} lang={lang} />;
  }

  // FIRST PAGE: Dedicated Login & Landing Page
  if (!currentUser) {
    return (
      <>
        <LoginPage
          currentUser={currentUser}
          onLoginSuccess={(user) => {
            setCurrentUser(user);
            localStorage.setItem('astro_custom_session', JSON.stringify(user));
            setIsLoginPage(false);
          }}
          onContinueAsGuest={() => {
            const guestUser: UserProfile = {
              uid: 'guest-' + Date.now(),
              email: 'guest@vedangjyotisham.com',
              role: UserRole.GUEST,
              baseRole: UserRole.GUEST,
              activeRole: UserRole.GUEST,
              tier: AccountTier.GUEST,
              hasPracticeEntitlement: false,
              hasConsultationEntitlement: false,
              authProvider: 'custom',
              displayName: 'Guest Seeker',
              createdAt: new Date().toISOString()
            };
            setCurrentUser(guestUser);
            localStorage.setItem('astro_custom_session', JSON.stringify(guestUser));
            setIsLoginPage(false);
          }}
          onOpenContactModal={() => setIsContactModalOpen(true)}
          lang={lang}
          setLang={setLang}
        />
        <ContactModal
          isOpen={isContactModalOpen}
          onClose={() => setIsContactModalOpen(false)}
          lang={lang}
          initialUserEmail={currentUser?.email || ''}
          initialUserName={currentUser?.displayName || ''}
        />
      </>
    );
  }

  // DEFAULT EXPERIENCE: V5.16 New Professional Dashboard
  if (!isLegacyDashboard && kundali) {
    return (
      <>
        <NewProfessionalDashboard
          kundali={kundali}
          lang={lang}
          setLang={setLang}
          chartStyle={chartStyle}
          setChartStyle={setChartStyle}
          currentUser={currentUser}
          activeTab={activeTab}
          savedKundalis={savedKundalis}
          onLoadSavedKundali={(k) => {
            if (k.id) setActiveKundaliId(k.id);
            setKundali(k);
          }}
          onApplyRectifiedKundali={handleApplyRectifiedKundali}
          onOpenBirthForm={handleOpenNewChart}
          onEditKundali={handleOpenEditKundali}
          onOpenMyVault={() => setIsMyVaultOpen(true)}
          onOpenAuth={() => setIsAuthModalOpen(true)}
          onOpenLegacyDashboard={handleOpenLegacyDashboard}
          onOpenGoldenRegression={() => setIsGoldenRegressionOpen(true)}
          onOpenAdminPortal={() => setIsAdminPortalOpen(true)}
          onOpenWhatsAppModal={() => setIsWhatsAppModalOpen(true)}
          onOpenBackupRestore={() => setIsBackupRestoreOpen(true)}
          onLogout={handleLogout}
          onSwitchRole={handleSwitchRole}
        />

        {/* Modals and Drawers accessible from New Professional Dashboard */}
        <BirthFormModal
          isOpen={isBirthFormOpen}
          onClose={() => {
            setIsBirthFormOpen(false);
            setEditingBirthDetails(null);
          }}
          onSubmit={handleBirthFormSubmit}
          initialDetails={editingBirthDetails}
          lang={lang}
        />
        <MyKundalisDrawer
          isOpen={isMyVaultOpen}
          onClose={() => setIsMyVaultOpen(false)}
          currentUser={currentUser}
          currentActiveKundali={kundali}
          onSelectKundali={(selected: any) => {
            trackKundaliAccess(selected.id).catch(console.error);
            setActiveKundaliId(selected.id);
            let loaded: CompleteKundali;
            try {
              loaded = calculateKundali(selected.kundaliData.birthDetails, getActiveCalculationContext());
            } catch {
              loaded = selected.kundaliData;
            }
            loaded.id = selected.id;
            loaded.cloudId = selected.id;
            setKundali(loaded);
          }}
          onDeleteKundali={handleDeleteKundaliFromApp}
          lang={lang}
          onOpenAuth={() => setIsAuthModalOpen(true)}
        />
        <AuthModal
          isOpen={isAuthModalOpen}
          onClose={() => setIsAuthModalOpen(false)}
          currentUser={currentUser}
          onLoginSuccess={(user) => {
            if (user) {
              setCurrentUser(user);
              localStorage.setItem('astro_custom_session', JSON.stringify(user));
            } else {
              handleLogout();
            }
            setIsAuthModalOpen(false);
          }}
          lang={lang}
        />
        {isAdminPortalOpen && ['super_admin', 'admin', 'super_user'].includes(String(currentUser?.role)) && (
          <AdminPortal
            currentUser={currentUser}
            onSelectKundali={(selected: any) => {
              trackKundaliAccess(selected.id).catch(console.error);
              try {
                setKundali(calculateKundali(selected.kundaliData.birthDetails, getActiveCalculationContext()));
              } catch {
                setKundali(selected.kundaliData);
              }
            }}
            lang={lang}
            onClose={() => setIsAdminPortalOpen(false)}
            onOpenGoldenRegression={() => setIsGoldenRegressionOpen(true)}
            onOpenBackupRestore={() => setIsBackupRestoreOpen(true)}
          />
        )}
        <WhatsAppIntegrationModal
          isOpen={isWhatsAppModalOpen}
          onClose={() => setIsWhatsAppModalOpen(false)}
          currentKundali={kundali}
        />
        <BackupRestoreModal
          isOpen={isBackupRestoreOpen}
          onClose={() => setIsBackupRestoreOpen(false)}
          savedKundalis={savedKundalis}
          currentUser={currentUser}
          onRestoreComplete={(updated) => {
            setSavedKundalis(deduplicateKundalis(updated));
            if (updated.length > 0 && !kundali) {
              setKundali(updated[0]);
            }
          }}
          lang={lang}
        />
        <GoldenKundaliModal
          isOpen={isGoldenRegressionOpen}
          onClose={() => setIsGoldenRegressionOpen(false)}
          lang={lang}
          currentUser={currentUser}
        />
        <ContactModal
          isOpen={isContactModalOpen}
          onClose={() => setIsContactModalOpen(false)}
          lang={lang}
          initialUserEmail={currentUser?.email || ''}
          initialUserName={currentUser?.displayName || ''}
        />
      </>
    );
  }

  return (
    <div className="min-h-screen bg-astro-card text-astro-text flex flex-col font-sans selection:bg-astro-primary selection:text-white overflow-x-hidden">
      {/* Return to New V5.16 Dashboard Banner */}
      <div className="bg-astro-primary text-white px-4 py-2.5 text-xs flex items-center justify-between shadow-xs z-40">
        <div className="flex items-center gap-2">
          <span className="font-bold uppercase tracking-wider bg-astro-accent text-white px-2 py-0.5 rounded text-[10px]">
            {lang === 'hi' ? 'लेगेसी मोड' : 'Legacy Fallback Mode'}
          </span>
          <span>
            {lang === 'hi' ? 'आप पुराने लेगेसी डैशबोर्ड का उपयोग कर रहे हैं।' : 'You are currently viewing the legacy fallback dashboard.'}
          </span>
        </div>
        <button
          onClick={() => setIsLegacyDashboard(false)}
          className="px-3 py-1 rounded bg-astro-accent hover:bg-[#725010] text-white font-bold text-xs transition-colors cursor-pointer"
        >
          {lang === 'hi' ? '← नए V5.16 डैशबोर्ड पर लौटें' : '← Return to New V5.16 Dashboard'}
        </button>
      </div>
      {/* App Navigation Header */}
      <Header
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        chartStyle={chartStyle}
        setChartStyle={setChartStyle}
        lang={lang}
        setLang={setLang}
        viewMode={viewMode}
        setViewMode={setViewMode}
        onOpenBirthForm={() => {
          setIsStartPopupOpen(false);
          setIsBirthFormOpen(true);
        }}
        kundali={kundali}
        savedKundalis={savedKundalis}
        onLoadSavedKundali={(k) => setKundali(k)}
        onApplyRectifiedKundali={handleApplyRectifiedKundali}
        onPrintReport={() => setIsPrintView(true)}
        currentUser={currentUser}
        activeProfile={activeProfile}
        onSelectProfile={(profile, computedKundali) => {
          setActiveProfile(profile);
          if (computedKundali) {
            setKundali(computedKundali);
          }
        }}
        onOpenAuth={() => setIsAuthModalOpen(true)}
        onOpenAdminPortal={() => setIsAdminPortalOpen(true)}
        onOpenMyVault={() => setIsMyVaultOpen(true)}
        onOpenWhatsAppModal={() => setIsWhatsAppModalOpen(true)}
        onOpenBackupRestore={() => setIsBackupRestoreOpen(true)}
        onOpenGoldenRegression={() => setIsGoldenRegressionOpen(true)}
        onLogout={handleLogout}
        onSwitchRole={handleSwitchRole}
      />

      {/* Tester Simulation Mode Notification Strip */}
      {currentUser?.isTester && currentUser?.testMode && (
        <div className="bg-astro-secondary-surface0/15 border-b border-astro-accent/30 px-4 py-2 text-xs flex items-center justify-between text-[#263633] font-medium">
          <div className="flex items-center gap-2">
            <span className="px-2 py-0.5 rounded bg-astro-secondary text-white font-bold text-[10px] uppercase tracking-wider">
              TEST MODE
            </span>
            <span>
              {lang === 'hi' ? 'सक्रिय अनुकरण:' : 'Simulated View:'}{' '}
              <strong className="text-[#263633]">
                {getRoleLabel(currentUser.activeRole || currentUser.role, lang)}
              </strong>{' '}
              <span className="opacity-75 font-mono text-[11px]">(Base: TESTER)</span>
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => handleSwitchRole(UserRole.TESTER)}
              className="px-2.5 py-1 rounded-lg bg-astro-secondary/20 hover:bg-amber-300 text-[#263633] text-xs font-bold transition-all"
            >
              {lang === 'hi' ? 'परीक्षक मोड पर लौटें' : 'Exit Simulation (Tester)'}
            </button>
          </div>
        </div>
      )}

      {/* Main Content Area */}
      <main className="flex-1 w-full max-w-7xl mx-auto px-4 sm:px-6 py-4 space-y-4 sm:space-y-6">
        {/* Blank Stage Banner when no Kundali loaded */}
        {!kundali && (
          <div className="min-h-[60vh] bg-astro-bg border border-astro-border rounded-2xl p-8 sm:p-12 text-center flex flex-col items-center justify-center space-y-6 shadow-sm relative overflow-hidden my-auto">
            <SwastikLogo size="lg" />
            <div className="max-w-md space-y-2">
              <h2 className="text-2xl font-bold text-astro-primary font-serif">
                Vedanga Jyotisha Horoscope Engine
              </h2>
              <p className="text-sm text-astro-secondary-text">
                {lang === 'hi'
                  ? 'कोई कुंडली सक्रिय नहीं है। मौजूदा कुंडली खोलें या नई जन्म कुंडली तैयार करें, या नीचे दैनिक पंचांग और मुहूर्त देखें।'
                  : 'No horoscope is currently active. Open an existing Kundali, calculate a fresh birth chart, or check Today\'s Panchang below.'}
              </p>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-3 pt-2">
              <button
                onClick={() => setIsStartPopupOpen(true)}
                className="px-5 py-2.5 rounded-xl bg-astro-primary hover:bg-[#123d3c] text-white font-bold text-sm flex items-center gap-2 shadow-sm transition-all transform hover:scale-[1.02]"
              >
                <Compass className="w-4 h-4" />
                <span>{lang === 'hi' ? 'कुंडली खोलें या बनाएं' : 'Open or Create Kundali'}</span>
              </button>
              <button
                onClick={() => setActiveTab('panchang')}
                className="px-5 py-2.5 rounded-xl bg-[#FEF3C7] border border-[#FDE68A] text-[#B45309] hover:bg-[#FDE68A] font-bold text-sm flex items-center gap-2 shadow-sm transition-all"
              >
                <Calendar className="w-4 h-4 text-[#D97706]" />
                <span>{lang === 'hi' ? 'दैनिक पंचांग देखें' : 'View Daily Panchang'}</span>
              </button>
            </div>
            <div className="pt-4 border-t border-astro-border w-full max-w-sm flex justify-center">
              <TodaysPanchangWidget lang={lang} />
            </div>
          </div>
        )}

        {/* Active Native Jataka Profile Compressed Header Strip */}
        {kundali && (
          <JatakaProfileHeader
            kundali={kundali}
            lang={lang}
            dob={kundali.birthDetails?.date ? formatDOB(kundali.birthDetails.date) : '12-Mar-2015'}
            savedKundalis={savedKundalis}
            currentUser={currentUser}
            onLoadSavedKundali={(k) => {
              if (k.id) setActiveKundaliId(k.id);
              setKundali(k);
            }}
            activeWorkspaceMode={
              activeTab === 'enterprise-studio' 
                ? 'enterprise-studio' 
                : activeTab === 'workbench' 
                ? 'astrologer' 
                : (activeTab === 'panchang' || activeTab === 'daily-panchang')
                ? 'daily-panchang'
                : 'tools'
            }
            onSelectWorkspaceMode={(mode) => {
              if (mode === 'enterprise-studio') setActiveTab('enterprise-studio');
              else if (mode === 'astrologer') setActiveTab('workbench');
              else setActiveTab('charts');
            }}
            workspaceName={
              activeTab === 'workbench'
                ? (lang === 'hi' ? 'कुंडली कार्यक्षेत्र' : 'Astrologers tools and workspace')
                : (lang === 'hi' ? 'विश्लेषण एवं परिणाम' : 'Synthesis & Analytics')
            }
            onToggleWorkspace={() => setActiveTab((prev) => (prev === 'workbench' ? 'analytics' : 'workbench'))}
            onEdit={handleOpenEditKundali}
          />
        )}

        {/* Contextual Sub-Navigation Ribbon (Always visible for Tools & Panchang access) */}
        <FeatureNavigationStrip
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          viewMode={viewMode}
          lang={lang}
        />

        {/* Tab View Switcher */}
        {activeTab === 'enterprise-studio' && kundali && (
          <VedicMilestoneDestinyStudio
            kundali={kundali}
            lang={lang}
            chartStyle={chartStyle}
            onOpenConsultation={(prompt) => {
              if (prompt) setInitialConsultationPrompt(prompt);
              setActiveTab('ai-consult');
            }}
          />
        )}

        {activeTab === 'workbench' && kundali && (
          <BasicAnalysisView
            kundali={kundali}
            lang={lang}
            chartStyle={chartStyle}
            setChartStyle={setChartStyle}
            onOpenPrintReport={() => setIsPrintView(true)}
            onEditKundali={handleOpenEditKundali}
            isBasicUser={false}
          />
        )}

        {activeTab === 'advanced-life-analysis' && kundali && (
          <AdvancedLifeAnalysisView
            kundali={kundali}
            lang={lang}
            chartStyle={chartStyle}
            setChartStyle={setChartStyle}
            currentUser={currentUser}
            onOpenConsultation={(prompt) => {
              if (prompt) setInitialConsultationPrompt(prompt);
              setActiveTab('ai-consult');
            }}
          />
        )}

        {activeTab === 'astrologer-dossier' && kundali && (
          <AstrologerDiagnosticDossier kundali={kundali} lang={lang} />
        )}

        {activeTab === 'spirituality' && kundali && (
          <SpiritualityIntelligenceView
            kundali={kundali}
            lang={lang}
            onOpenConsultation={(prompt) => {
              if (prompt) setInitialConsultationPrompt(prompt);
              setActiveTab('ai-consult');
            }}
          />
        )}

        {activeTab === 'simple-reading' && kundali && (
          <SimpleAnalysisView
            kundali={kundali}
            lang={lang}
            chartStyle={chartStyle}
            setChartStyle={setChartStyle}
            onOpenConsultation={(prompt) => {
              if (prompt) setInitialConsultationPrompt(prompt);
              setActiveTab('ai-consult');
            }}
            onPrintReport={() => setIsPrintView(true)}
          />
        )}

        {(activeTab === 'panchang' || activeTab === 'daily-panchang') && (
          <PanchangMuhurtaView kundali={kundali} lang={lang} />
        )}

        {(activeTab === 'charts' || activeTab === 'full-panchang') && kundali && (
          <ChartViewer
            kundali={kundali}
            chartStyle={chartStyle}
            setChartStyle={setChartStyle}
            lang={lang}
            setLang={setLang}
            currentUser={currentUser}
            initialDivisionalCode={activeTab === 'full-panchang' ? 'FullPanchang' : undefined}
          />
        )}

        {activeTab === 'ashtakvarga' && kundali && <AshtakvargaView kundali={kundali} lang={lang} />}

        {activeTab === 'dashas' && kundali && (
          <DashaTimeline
            kundali={kundali}
            lang={lang}
            targetAnalysisDateTime={targetAnalysisDateTime}
            onTargetAnalysisDateTimeChange={setTargetAnalysisDateTime}
            onNavigateToGochar={() => setActiveTab('gochar')}
            onOpenBtrDrawer={() => setIsBtrDrawerOpen(true)}
          />
        )}
        
        {activeTab === 'doshas' && kundali && (
          <DoshaSection kundali={kundali} lang={lang} />
        )}

        {activeTab === 'shadbala' && kundali && (
          <ShadbalaView kundali={kundali} lang={lang} />
        )}

        {activeTab === 'kp' && kundali && (
          <KPView kundali={kundali} lang={lang} />
        )}

        {activeTab === 'specialized' && kundali && (
          <SpecializedDiagnosticsView
            kundali={kundali}
            lang={lang}
            chartStyle={chartStyle}
            setChartStyle={setChartStyle}
            onOpenAnalytics={() => setActiveTab('analytics')}
          />
        )}

        {activeTab === 'shubha-essentials' && kundali && (
          <ShubhaEssentialsHub
            kundali={kundali}
            lang={lang}
            initialTab="overview"
            onNavigateTab={(tabId) => setActiveTab(tabId)}
          />
        )}

        {activeTab === 'navtara' && kundali && (
          <ShubhaEssentialsHub
            kundali={kundali}
            lang={lang}
            initialTab="navtara"
            onNavigateTab={(tabId) => setActiveTab(tabId)}
          />
        )}

        {activeTab === 'gochar' && kundali && (
          <GocharView
            kundali={kundali}
            lang={lang}
            targetAnalysisDateTime={targetAnalysisDateTime}
            onTargetAnalysisDateTimeChange={setTargetAnalysisDateTime}
            onNavigateToTab={(tabId) => setActiveTab(tabId)}
          />
        )}

        {activeTab === 'planets' && kundali && <PlanetaryTable kundali={kundali} lang={lang} />}
        
        {activeTab === 'muhurta' && kundali && (
          <ShubhaEssentialsHub
            kundali={kundali}
            lang={lang}
            initialTab="muhurta"
            onNavigateTab={(tabId) => setActiveTab(tabId)}
          />
        )}

        {activeTab === 'publication-dossier' && kundali && (
          <PublicationDossierView kundali={kundali} lang={lang} chartStyle={chartStyle} />
        )}

        {activeTab === 'gemstones' && kundali && (
          <ShubhaEssentialsHub
            kundali={kundali}
            lang={lang}
            initialTab="gemstones"
            onNavigateTab={(tabId) => setActiveTab(tabId)}
          />
        )}

        {activeTab === 'remedies' && kundali && (
          <ShubhaEssentialsHub
            kundali={kundali}
            lang={lang}
            initialTab="remedies"
            onNavigateTab={(tabId) => setActiveTab(tabId)}
          />
        )}
        
        {activeTab === 'karka' && kundali && <KarkaView kundali={kundali} lang={lang} />}
        
        {activeTab === 'dashaChart' && kundali && (
          <DashaChartView 
            kundali={kundali} 
            chartStyle={chartStyle} 
            lang={lang}
            showDegrees={true}
            onSelectPlanet={() => {}}
            onSelectHouse={() => {}}
          />
        )}

        {activeTab === 'prashna' && <PrashnaModule lang={lang} />}

        {activeTab === 'matching' && (
          <MatchingModule
            currentKundali={kundali}
            savedKundalis={savedKundalis}
            lang={lang}
            onOpenBirthForm={() => {
              setIsStartPopupOpen(false);
              setIsBirthFormOpen(true);
            }}
          />
        )}

        {activeTab === 'numerology' && (
          <NumerologyView kundali={kundali} lang={lang} />
        )}

        {activeTab === 'palmistry' && (
          <PalmistryView kundali={kundali} lang={lang} />
        )}

        {activeTab === 'vastu' && (
          <VastuView kundali={kundali} lang={lang} />
        )}

        {activeTab === 'ai-consult' && kundali && <GeminiAstrologer kundali={kundali} lang={lang} initialPrompt={initialConsultationPrompt} />}

        {activeTab === 'rectification' && <BirthTimeRectification lang={lang} />}

        {activeTab === 'vedic-engine' && <VedicAnalysisEngine kundali={kundali} lang={lang} />}

        {activeTab === 'core-view' && kundali && (
          <AstrologerCoreView
            kundali={kundali}
            lang={lang}
            onOpenPublishedConsultation={() => setActiveTab('analytics')}
          />
        )}

        {activeTab === 'analytics' && kundali && <AnalyticsReportView kundali={kundali} lang={lang} />}

        {activeTab === 'trines' && kundali && <TrinesView kundali={kundali} lang={lang} />}

        {activeTab === 'dignity' && kundali && (
          <PlanetStrengthDashboard kundali={kundali} lang={lang} />
        )}
      </main>

      {/* Start Popup Choice Modal */}
      <StartPopupModal
        isOpen={isStartPopupOpen}
        onClose={() => setIsStartPopupOpen(false)}
        onCreateNew={() => setIsBirthFormOpen(true)}
        onLoadSavedKundali={(k) => {
          try {
            setKundali(calculateKundali(k.birthDetails, getActiveCalculationContext()));
          } catch {
            setKundali(k);
          }
        }}
        savedKundalis={savedKundalis}
        lang={lang}
        setLang={setLang}
        currentUser={currentUser}
      />

      {/* Birth Details Input Modal */}
      <BirthFormModal
        isOpen={isBirthFormOpen}
        onClose={() => {
          setIsBirthFormOpen(false);
          setEditingBirthDetails(null);
        }}
        onSubmit={handleBirthFormSubmit}
        initialDetails={editingBirthDetails}
        lang={lang}
      />

      {/* Identity Authentication & Profile Selector Modal */}
      <AuthModal
        isOpen={isAuthModalOpen}
        onClose={() => setIsAuthModalOpen(false)}
        currentUser={currentUser}
        onLoginSuccess={(user) => {
          if (user) {
            setCurrentUser(user);
            localStorage.setItem('astro_custom_session', JSON.stringify(user));
          } else {
            handleLogout();
          }
        }}
        lang={lang}
      />

      {/* My Cloud Kundalis Vault Drawer */}
      <MyKundalisDrawer
        isOpen={isMyVaultOpen}
        onClose={() => setIsMyVaultOpen(false)}
        currentUser={currentUser}
        currentActiveKundali={kundali}
        activeProfile={activeProfile}
        onSelectKundali={(selected: any) => {
          trackKundaliAccess(selected.id).catch(console.error);
          setActiveKundaliId(selected.id);
          let loaded: CompleteKundali;
          try {
            loaded = calculateKundali(selected.kundaliData.birthDetails, getActiveCalculationContext());
          } catch {
            loaded = selected.kundaliData;
          }
          loaded.id = selected.id;
          loaded.cloudId = selected.id;
          setKundali(loaded);
          setActiveTab('analytics');
        }}
        onDeleteKundali={handleDeleteKundaliFromApp}
        lang={lang}
        onOpenAuth={() => {
          setIsMyVaultOpen(false);
          setIsAuthModalOpen(true);
        }}
      />

      {/* Admin Kundali Vault & Governance Portal */}
      {isAdminPortalOpen && ['super_admin', 'admin', 'super_user'].includes(String(currentUser?.role)) && (
        <AdminPortal
          currentUser={currentUser}
          onSelectKundali={(selected: any) => {
          trackKundaliAccess(selected.id).catch(console.error);
          try {
            setKundali(calculateKundali(selected.kundaliData.birthDetails, getActiveCalculationContext()));
          } catch {
            setKundali(selected.kundaliData);
          }
          setActiveTab('analytics');
        }}
          lang={lang}
          onClose={() => setIsAdminPortalOpen(false)}
          onOpenGoldenRegression={() => setIsGoldenRegressionOpen(true)}
          onOpenBackupRestore={() => setIsBackupRestoreOpen(true)}
        />
      )}

      {/* Contact Form Modal */}
      <ContactModal
        isOpen={isContactModalOpen}
        onClose={() => setIsContactModalOpen(false)}
        lang={lang}
        initialUserEmail={currentUser?.email || ''}
        initialUserName={currentUser?.displayName || ''}
      />

      {/* WhatsApp AI Agent Modal */}
      <WhatsAppIntegrationModal
        isOpen={isWhatsAppModalOpen}
        onClose={() => setIsWhatsAppModalOpen(false)}
        currentKundali={kundali}
      />

      {/* Local Backup & Restore Modal */}
      <BackupRestoreModal
        isOpen={isBackupRestoreOpen}
        onClose={() => setIsBackupRestoreOpen(false)}
        savedKundalis={savedKundalis}
        currentUser={currentUser}
        onRestoreComplete={(updated) => {
          setSavedKundalis(deduplicateKundalis(updated));
          if (updated.length > 0 && !kundali) {
            setKundali(updated[0]);
          }
        }}
        lang={lang}
      />

      {/* Golden Kundali Regression Diagnostic Modal */}
      <GoldenKundaliModal
        isOpen={isGoldenRegressionOpen}
        onClose={() => setIsGoldenRegressionOpen(false)}
        lang={lang}
        currentUser={currentUser}
      />

      {/* Birth Time Rectification (BTR) Drawer */}
      {kundali && (
        <BtrDrawer
          key={getBtrProfileInstanceKey(kundali, currentUser?.uid)}
          isOpen={isBtrDrawerOpen}
          onClose={() => setIsBtrDrawerOpen(false)}
          kundali={kundali}
          lang={lang}
          currentUser={currentUser}
          onApplyRectifiedKundali={(rectifiedKundali) => setKundali(rectifiedKundali)}
        />
      )}

      {/* Global Auspicious Essentials Modal Handler */}
      {kundali && (
        <AuspiciousEssentialsBar
          kundali={kundali}
          lang={lang}
          className="hidden"
          onOpenNumerology={() => setActiveTab('numerology')}
          onOpenGemstones={() => setActiveTab('shubha-essentials')}
        />
      )}

      {/* Quick Launch Floating AI Astrologer Button */}
      {kundali && activeTab !== 'ai-consult' && (isBasicTierUser(currentUser) || !currentUser || showAskAcharyaWidget) && (
        <button
          id="floating-ai-astrologer-btn"
          onClick={() => setActiveTab('ai-consult')}
          className="fixed bottom-6 right-6 z-40 flex items-center gap-2 px-4 py-3 rounded-full bg-astro-primary hover:bg-astro-secondary text-white font-bold text-xs sm:text-sm shadow-xl hover:shadow-2xl border-2 border-astro-accent/40 transition-all transform hover:scale-105 active:scale-95 group backdrop-blur-xs cursor-pointer"
          title={lang === 'hi' ? 'एआई ज्योतिषाचार्य से परामर्श लें' : 'Consult AI Astrologer'}
        >
          <Sparkles className="w-4 h-4 text-[#E6CA65] animate-pulse group-hover:rotate-12 transition-transform" />
          <span className="font-semibold">{lang === 'hi' ? 'AI ज्योतिषी से पूछें' : 'Ask AI Astrologer'}</span>
        </button>
      )}

      {/* Footer */}
      <footer className="bg-astro-bg border-t border-astro-border py-6 text-center text-xs text-astro-secondary-text">
        <div className="w-full px-4 sm:px-6 flex flex-col sm:flex-row items-center justify-between gap-2">
          <span>© 2026 Vedanga Jyotisha • Contact: vedangajyotisha108@gmail.com</span>
          <button
            onClick={() => setIsContactModalOpen(true)}
            className="text-astro-primary hover:underline font-semibold"
          >
            Send Inquiry / Contact Form →
          </button>
          <span className="text-astro-accent font-serif">ॐ Shanti Shanti Shanti ॐ</span>
        </div>
      </footer>
    </div>
  );
}
